/**
 * pendingCardAction — 비로그인 상태에서 누른 카드 액션을 로그인 후 자동 재개하는 마커.
 *
 * 왜 pendingAuthIntent(navigate) 가 아닌가:
 *   auth 는 현재 화면 **위에 오버레이**로 열리므로 카드 상세는 그대로 마운트돼 있다.
 *   여기서 navigate 인텐트를 쓰면 같은 화면을 한 번 더 쌓게 된다. 그래서 auth intent 는
 *   `{ type: "none" }` 으로 두고, 이 마커를 남겨 카드 상세가 인증 완료를 감지했을 때
 *   액션을 다시 실행한다. (템플릿의 pendingKujiAction 과 동일한 패턴)
 *
 * sessionStorage 인 이유도 동일 — webview lifecycle 스코프 + OAuth redirect 체인 생존.
 * cardUid 로 스코프해 다른 카드로 누수되지 않게 한다.
 */

const KEY = "peeka.pendingCardAction.v1";
const TTL_MS = 30 * 60 * 1000;

/**
 * 재개할 액션 — 컬렉션 추가(기획안 §6.1) 하나뿐이다.
 *
 * ⚠ 2026-08-24 에 "addToWatchlist" 를 걷었다. SC-019 v0.4 rev2 가 별도 관심/찜 지정을
 *   없애고 컬렉션 담기를 곧 관심 표시로 정의해, 재개할 관심 액션 자체가 사라졌다.
 */
export type CardResumeAction = "addToCollection";

const RESUME_ACTIONS: readonly CardResumeAction[] = ["addToCollection"];

interface StoredCardAction {
  cardUid: string;
  action: CardResumeAction;
  createdAt: number;
}

function storage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function setPendingCardAction(cardUid: string, action: CardResumeAction): void {
  const s = storage();
  if (!s || !cardUid) return;
  const stored: StoredCardAction = { cardUid, action, createdAt: Date.now() };
  try {
    s.setItem(KEY, JSON.stringify(stored));
  } catch {
    /* quota — 재개 못 해도 사용자가 다시 누르면 된다 */
  }
}

/**
 * 같은 cardUid 의 마커가 있으면 소비(제거)하고 action 반환.
 * 없거나 다른 카드·만료·malformed 면 null. 소비는 1회성.
 */
export function consumePendingCardAction(cardUid: string): CardResumeAction | null {
  const s = storage();
  if (!s) return null;
  const raw = s.getItem(KEY);
  if (!raw) return null;

  let parsed: StoredCardAction;
  try {
    parsed = JSON.parse(raw) as StoredCardAction;
  } catch {
    s.removeItem(KEY);
    return null;
  }

  if (
    typeof parsed !== "object" ||
    parsed === null ||
    typeof parsed.cardUid !== "string" ||
    typeof parsed.createdAt !== "number" ||
    !RESUME_ACTIONS.includes(parsed.action)
  ) {
    s.removeItem(KEY);
    return null;
  }
  if (Date.now() - parsed.createdAt > TTL_MS) {
    s.removeItem(KEY);
    return null;
  }
  // 다른 카드의 마커는 남겨둔다 — 그 카드 상세가 소비한다.
  if (parsed.cardUid !== cardUid) return null;

  s.removeItem(KEY);
  return parsed.action;
}

export function discardPendingCardAction(): void {
  storage()?.removeItem(KEY);
}

export const __PENDING_CARD_ACTION_KEY__ = KEY;
